import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Trash2, Save, ChevronRight, Shield } from 'lucide-react';
import { saveToLocalStorage, loadFromLocalStorage, generateId } from '@/utils/storage';
import { isAdminSession } from '@/utils/auth';
import type { ContractTerms, PurchaseItem, PaymentInstallment } from '@/types';
import { amountToUppercase, calcAmount, calcTotalAmount } from '@/utils/amountUtils';

const STORAGE_KEY = 'contract_clause_presets';
const DRAFT_KEY = 'contract_clause_draft';

const DEFAULT_PRESETS: ClausePreset[] = [
  {
    id: 'preset_001',
    name: '标准采购合同付款条款',
    terms: {
      paymentMethod: '分期付款',
      paymentDetails: '首付30%（合同签订后5个工作日内），中期30%（货到验收合格后），尾款40%（质保期满无质量问题后）',
      validityPeriod: '自签订之日起1年',
      specialClauses: '质量保证期12个月，自验收合格之日起计算。供方对产品质量问题承担无条件退换货责任。',
      purchaseItems: [{ 产品名称: '', 规格型号: '', 数量: '', 单价: '', 金额: '' }],
      totalAmount: '',
      totalAmountUppercase: '',
      installments: [
        { period: 1, percentage: '30%', amount: '' },
        { period: 2, percentage: '30%', amount: '' },
        { period: 3, percentage: '40%', amount: '' },
      ],
    },
    createdAt: new Date().toISOString(),
  },
];

interface ClausePreset {
  id: string;
  name: string;
  terms: ContractTerms;
  createdAt: string;
}

const EMPTY_TERMS: ContractTerms = {
  paymentMethod: '一次性付款',
    paymentDetails: '',
    validityPeriod: '',
    specialClauses: '',
    purchaseItems: [{ 产品名称: '', 规格型号: '', 数量: '', 单价: '', 金额: '' }],
    totalAmount: '',
    totalAmountUppercase: '',
    instalments: [],
};

// 块定义
const BLOCKS = [
  { key: 'base', label: '基础信息', icon: '📋' },
  { key: 'partyA', label: '甲方信息', icon: '🏢' },
  { key: 'partyB', label: '乙方信息', icon: '🏪' },
  { key: 'core', label: '合同核心信息', icon: '📄' },
] as const;

export default function ClauseManager() {
  const navigate = useNavigate();
  const [presets, setPresets] = useState<ClausePreset[]>([]);
  const [editing, setEditing] = useState<ClausePreset | null>(null);
  const [terms, setTerms] = useState<ContractTerms>(EMPTY_TERMS);
  const [presetName, setPresetName] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [expandedBlock, setExpandedBlock] = useState<string>('core');

  // 自动计算明细金额 + 总金额 + 联动分期金额
  const updatePurchaseItems = useCallback((items: PurchaseItem[]) => {
    const updated = items.map(it => ({
      ...it,
      金额: it.数量 && it.单价 ? calcAmount(it.数量, it.单价) : it.金额,
    }));
    const total = calcTotalAmount(updated);
    const totalNum = parseFloat(total) || 0;

    setTerms(prev => {
      const newState: ContractTerms = {
        ...prev,
        purchaseItems: updated,
        totalAmount: total,
        totalAmountUppercase: total ? amountToUppercase(total) : '',
      };
      // 如果当前是分期付款且已有分期设置，自动重算每期金额
      if (prev.paymentMethod === '分期付款' && prev.installments && prev.installments.length > 0) {
        newState.installments = prev.installments.map(inst => ({
          ...inst,
          amount: inst.percentage ? (totalNum * parseFloat(inst.percentage) / 100).toFixed(2) : '',
        }));
      }
      return newState;
    });
  }, []);

  // 自动计算分期金额
  const updateInstallements = useCallback((installments: PaymentInstallment[]) => {
    const total = parseFloat(terms.totalAmount) || 0;
    const updated = installments.map(inst => ({
      ...inst,
      amount: inst.percentage ? (total * parseFloat(inst.percentage) / 100).toFixed(2) : '',
    }));
    setTerms(prev => ({ ...prev, installments: updated }));
  }, [terms.totalAmount]);

  useEffect(() => {
    const data = loadFromLocalStorage<ClausePreset[]>(STORAGE_KEY);
    if (!data || data.length === 0) {
      saveToLocalStorage(STORAGE_KEY, DEFAULT_PRESETS);
      setPresets(DEFAULT_PRESETS);
    } else {
      setPresets(data);
    }
  }, []);

  const savePresets = (list: ClausePreset[]) => {
    setPresets(list);
    saveToLocalStorage(STORAGE_KEY, list);
  };

  const handleSave = () => {
    if (!presetName.trim()) {
      alert('请填写预设名称');
      return;
    }
    // 分期付款校验
    if (terms.paymentMethod === '分期付款' && terms.installments && terms.installments.length > 0) {
      const totalAmount = parseFloat(terms.totalAmount || '0') || 0;
      const installmentTotal = terms.installments.reduce((sum, inst) => sum + (parseFloat(inst.amount || '0') || 0), 0);
      const percentageTotal = terms.installments.reduce((sum, inst) => sum + (parseFloat(inst.percentage?.replace('%', '') || '0') || 0), 0);
      if (totalAmount > 0 && installmentTotal > totalAmount) {
        alert('分期付款总金额（¥' + installmentTotal.toFixed(2) + '）超过采购总金额（¥' + totalAmount.toFixed(2) + '），请调整');
        return;
      }
      if (percentageTotal > 100) {
        alert('分期付款比例之和（' + percentageTotal + '%）超过100%，请调整');
        return;
      }
    }
    const newPreset: ClausePreset = {
      id: editing?.id || generateId(),
      name: presetName.trim(),
      terms: { ...terms },
      createdAt: new Date().toISOString(),
    };
    if (editing) {
      savePresets(presets.map(p => p.id === editing.id ? newPreset : p));
    } else {
      savePresets([newPreset, ...presets]);
    }
    setSavedMsg(editing ? '已更新预设' : '已保存预设');
    setTimeout(() => setSavedMsg(''), 2000);
    setEditing(null);
    setPresetName('');
    setTerms(EMPTY_TERMS);
  };

  const handleEdit = (preset: ClausePreset) => {
    setEditing(preset);
    setPresetName(preset.name);
    setTerms({ ...preset.terms, purchaseItems: preset.terms.purchaseItems || EMPTY_TERMS.purchaseItems, installments: (preset.terms as any).installments || (preset.terms as any).instalments || [] });
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('确定删除该预设？')) return;
    savePresets(presets.filter(p => p.id !== id));
    if (editing?.id === id) {
      setEditing(null);
      setPresetName('');
      setTerms(EMPTY_TERMS);
    }
  };

  const handleUseInGenerator = () => {
    // 分期付款校验
    if (terms.paymentMethod === '分期付款' && terms.installments && terms.installments.length > 0) {
      const totalAmount = parseFloat(terms.totalAmount || '0') || 0;
      const installmentTotal = terms.installments.reduce((sum, inst) => sum + (parseFloat(inst.amount || '0') || 0), 0);
      const percentageTotal = terms.installments.reduce((sum, inst) => sum + (parseFloat(inst.percentage?.replace('%', '') || '0') || 0), 0);
      if (totalAmount > 0 && installmentTotal > totalAmount) {
        alert('分期付款总金额（¥' + installmentTotal.toFixed(2) + '）超过采购总金额（¥' + totalAmount.toFixed(2) + '），请调整');
        return;
      }
      if (percentageTotal > 100) {
        alert('分期付款比例之和（' + percentageTotal + '%）超过100%，请调整');
        return;
      }
    }
    // 将当前编辑的内容保存为临时草稿，带入生成合同页面
    saveToLocalStorage(DRAFT_KEY, terms);
    navigate('/generate');
  };

  // ---- 渲染 ----

  return (
    <div className="space-y-6">
      {/* 顶部 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">合同核心条款</h1>
          <p className="text-gray-500 text-sm mt-1">预先填写常用条款，生成合同时一键选用。分块管理，2列布局，采购明细自动算金额。</p>
        </div>
        <div className="flex items-center gap-3">
          {isAdminSession() && (
            <button
              onClick={() => navigate('/risk-rules')}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
            >
              <Shield className="w-4 h-4" />
              风险规则管理
            </button>
          )}
          <div className="text-sm text-gray-400">共 {presets.length} 条预设</div>
        </div>
      </div>

      {savedMsg && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {savedMsg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 左侧：预设列表 */}
        <div className="lg:col-span-1 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-700">已保存预设</h3>
            <button
              onClick={() => { setEditing(null); setPresetName(''); setTerms(EMPTY_TERMS); }}
              className="text-sm text-primary hover:underline"
            >
              ＋ 新建
            </button>
          </div>
          {presets.length === 0 && (
            <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">暂无预设条款</p>
            </div>
          )}
          {presets.map(preset => (
            <div
              key={preset.id}
              onClick={() => handleEdit(preset)}
              className={`p-4 rounded-xl border cursor-pointer transition ${
                editing?.id === preset.id
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-800">{preset.name}</span>
                <button
                  onClick={e => { e.stopPropagation(); handleDelete(preset.id); }}
                  className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="text-xs text-gray-400 mt-1">
                付款：{preset.terms.paymentMethod}
                {preset.terms.validityPeriod && ` · 有效期：${preset.terms.validityPeriod}`}
              </div>
            </div>
          ))}
        </div>

        {/* 右侧：编辑表单（分块两列） */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h3 className="font-medium text-gray-800 mb-2">
            {editing ? `编辑预设：${editing.name}` : '填写合同核心条款'}
          </h3>

          {/* 块导航 TAB */}
          <div className="flex gap-2 flex-wrap border-b border-gray-100 pb-3">
            {BLOCKS.map(b => (
              <button
                key={b.key}
                onClick={() => setExpandedBlock(b.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  expandedBlock === b.key
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {b.icon} {b.label}
              </button>
            ))}
          </div>

          {/* ---- 块1：基础信息 ---- */}
          {expandedBlock === 'base' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">合同编号</label>
                <input
                  type="text"
                  value={(terms as any).contractNumber || ''}
                  onChange={e => setTerms(prev => ({ ...prev, contractNumber: e.target.value } as any))}
                  placeholder="如：HT-2024001"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">合同签订日期</label>
                <input
                  type="date"
                  value={(terms as any).signingDate || ''}
                  onChange={e => setTerms(prev => ({ ...prev, signingDate: e.target.value } as any))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">合同生效日期</label>
                <input
                  type="date"
                  value={(terms as any).startDate || ''}
                  onChange={e => setTerms(prev => ({ ...prev, startDate: e.target.value } as any))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">合同到期日期</label>
                <input
                  type="date"
                  value={(terms as any).endDate || ''}
                  onChange={e => setTerms(prev => ({ ...prev, endDate: e.target.value } as any))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                />
              </div>
            </div>
          )}

          {/* ---- 块2：甲方信息（只读提示） ---- */}
          {expandedBlock === 'partyA' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
              ℹ️ 甲方信息在「公司信息」页面选择，此处无需重复填写。生成合同时自动带入。
            </div>
          )}

          {/* ---- 块3：乙方信息（只读提示） ---- */}
          {expandedBlock === 'partyB' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700">
              ℹ️ 乙方信息在「生成合同」页面填写，此处无需重复填写。
            </div>
          )}

          {/* ---- 块4：合同核心信息（主内容） ---- */}
          {expandedBlock === 'core' && (
            <div className="space-y-5">
              {/* 付款方式 + 有效期 ：两列 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">付款方式</label>
                  <select
                    value={terms.paymentMethod}
                    onChange={e => {
                      const method = e.target.value as ContractTerms['paymentMethod'];
                      setTerms(prev => ({
                        ...prev,
                        paymentMethod: method,
                        installments: method === '分期付款' ? (prev.installments || []) : (prev.installments || []),
                      }));
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none bg-white"
                  >
                    <option>一次性付款</option>
                    <option>分期付款</option>
                    <option>月结</option>
                    <option>季结</option>
                    <option>其他</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">合同有效期</label>
                  <input
                    type="text"
                    value={terms.validityPeriod}
                    onChange={e => setTerms(prev => ({ ...prev, validityPeriod: e.target.value }))}
                    placeholder="如：自签订之日起1年"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                  />
                </div>
              </div>

              {/* 付款详情 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  付款详情
                  {terms.paymentMethod === '分期付款' && <span className="text-red-500 ml-1">*</span>}
                </label>
                <textarea
                  value={terms.paymentDetails}
                  onChange={e => setTerms(prev => ({ ...prev, paymentDetails: e.target.value }))}
                  placeholder={terms.paymentMethod === '分期付款' ? '如：首付30%（合同签订后5个工作日内），中期30%（交付验收后），尾款40%（质保期满后）' : '可填写付款条件、账期等补充说明'}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none resize-none"
                />
              </div>

              {/* 分期付款明细 */}
              {terms.paymentMethod === '分期付款' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-bold text-orange-800">分期付款明细</h4>
                  {(() => {
                    const totalAmount = parseFloat(terms.totalAmount || '0') || 0;
                    const instList = terms.installments || [];
                    const installmentTotal = instList.reduce((sum, inst) => sum + (parseFloat(inst.amount || '0') || 0), 0);
                    const percentageTotal = instList.reduce((sum, inst) => sum + (parseFloat(inst.percentage?.replace('%', '') || '0') || 0), 0);
                    const isOverAmount = totalAmount > 0 && installmentTotal > totalAmount;
                    const isOverPercent = percentageTotal > 100;
                    if (!isOverAmount && !isOverPercent) return null;
                    return (
                      <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                        {isOverAmount && `⚠️ 分期总金额 ¥${installmentTotal.toFixed(2)} 超过采购总金额 ¥${totalAmount.toFixed(2)}`}
                        {isOverAmount && isOverPercent && '；'}
                        {isOverPercent && `⚠️ 分期比例之和 ${percentageTotal}% 超过100%`}
                      </div>
                    );
                  })()}
                  {(terms.installments || []).map((inst, idx) => (
                    <div key={idx} className="grid grid-cols-3 gap-3 items-end">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">第{idx + 1}期比例</label>
                        <input
                          type="text"
                          value={inst.percentage}
                          onChange={e => {
                            const updated = [...terms.installments];
                            updated[idx] = { ...updated[idx], percentage: e.target.value };
                            updateInstallements(updated);
                          }}
                          placeholder="如：30%"
                          className="w-full px-3 py-2 border border-orange-200 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">本期金额（小写）</label>
                        <input
                          type="text"
                          value={inst.amount}
                          readOnly
                          className="w-full px-3 py-2 border border-orange-100 rounded-lg text-sm bg-orange-50"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        {terms.installments.length > 1 && (
                          <button
                            onClick={() => {
                              const updated = terms.installments.filter((_, i) => i !== idx);
                              setTerms(prev => ({ ...prev, installments: updated }));
                            }}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const updated = [...terms.installments, { period: terms.installments.length + 1, percentage: '', amount: '' }];
                      setTerms(prev => ({ ...prev, installments: updated }));
                    }}
                    className="text-sm text-orange-700 hover:underline flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> 添加分期
                  </button>
                </div>
              )}

              {/* 采购明细表 */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 mb-2">采购明细</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs text-gray-500 font-medium">产品名称</th>
                        <th className="px-3 py-2 text-left text-xs text-gray-500 font-medium">规格型号</th>
                        <th className="px-3 py-2 text-left text-xs text-gray-500 font-medium w-20">数量</th>
                        <th className="px-3 py-2 text-left text-xs text-gray-500 font-medium w-24">单价</th>
                        <th className="px-3 py-2 text-left text-xs text-gray-500 font-medium w-24">金额</th>
                        <th className="px-3 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(terms.purchaseItems || []).map((item, idx) => (
                        <tr key={idx} className="border-t border-gray-100">
                          <td className="px-2 py-1.5">
                            <input
                              type="text"
                              value={item.产品名称}
                              onChange={e => {
                                const updated = [...(terms.purchaseItems || [])];
                                updated[idx] = { ...updated[idx], 产品名称: e.target.value };
                                updatePurchaseItems(updated);
                              }}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="text"
                              value={item.规格型号}
                              onChange={e => {
                                const updated = [...(terms.purchaseItems || [])];
                                updated[idx] = { ...updated[idx], 规格型号: e.target.value };
                                updatePurchaseItems(updated);
                              }}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              value={item.数量}
                              onChange={e => {
                                const updated = [...(terms.purchaseItems || [])];
                                updated[idx] = { ...updated[idx], 数量: e.target.value };
                                updatePurchaseItems(updated);
                              }}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              value={item.单价}
                              onChange={e => {
                                const updated = [...(terms.purchaseItems || [])];
                                updated[idx] = { ...updated[idx], 单价: e.target.value };
                                updatePurchaseItems(updated);
                              }}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="text"
                              value={item.金额}
                              readOnly
                              className="w-full px-2 py-1 border border-gray-100 rounded text-sm bg-gray-50"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            {(terms.purchaseItems || []).length > 1 && (
                              <button
                                onClick={() => {
                                  const updated = (terms.purchaseItems || []).filter((_, i) => i !== idx);
                                  updatePurchaseItems(updated);
                                }}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  onClick={() => {
                    const updated = [...(terms.purchaseItems || []), { 产品名称: '', 规格型号: '', 数量: '', 单价: '', 金额: '' }];
                    updatePurchaseItems(updated);
                  }}
                  className="mt-2 text-sm text-primary hover:underline flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> 添加明细行
                </button>
              </div>

              {/* 总金额（小写 + 大写） */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div>
                  <label className="block text-sm font-medium text-blue-800 mb-1">总金额（小写）¥</label>
                  <input
                    type="text"
                    value={terms.totalAmount}
                    onChange={e => {
                      const v = e.target.value;
                      setTerms(prev => ({
                        ...prev,
                        totalAmount: v,
                        totalAmountUppercase: v ? amountToUppercase(v) : '',
                      }));
                    }}
                    placeholder="自动计算或手动填写"
                    className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-300 outline-none bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-800 mb-1">总金额（大写）</label>
                  <div className="px-4 py-2 bg-white border border-blue-200 rounded-lg text-sm text-blue-900 min-h-[40px] flex items-center">
                    {terms.totalAmountUppercase || '自动计算'}
                  </div>
                </div>
              </div>

              {/* 特殊条款 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">特殊条款</label>
                <textarea
                  value={terms.specialClauses}
                  onChange={e => setTerms(prev => ({ ...prev, specialClauses: e.target.value }))}
                  placeholder="如：保密义务、知识产权归属、违约责任、争议解决方式等"
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none resize-none"
                />
              </div>
            </div>
          )}

          {/* 底部操作 */}
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition"
            >
              <Save className="w-4 h-4" />
              {editing ? '更新预设' : '保存预设'}
            </button>
            <button
              onClick={handleUseInGenerator}
              className="flex items-center gap-2 px-5 py-2.5 border border-primary text-primary rounded-lg font-medium hover:bg-primary/5 transition"
            >
              <ChevronRight className="w-4 h-4" />
              去生成合同
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Plus(props: { className?: string }) {
  return (
    <svg className={props.className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}
